/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef, useState } from 'react'
import { CustomButton, CustomButtonGroup, CustomInput, useEffectOnce } from '../../../globalUtils/globalExports'
import './AddLogicalExpression.css'
import { toast } from 'react-toastify'

interface AddLogicalExpressionProps {
    logicalExpression: string | null,
    handleUpdateLogicalExpression: (tokens: string[]) => void,
    handleClose: () => void,
}

const AddLogicalExpression: React.FC<AddLogicalExpressionProps> = (props) => {

    const {
        handleUpdateLogicalExpression,
        handleClose,
        logicalExpression
    } = props;

    const [tokens, setTokens] = useState<string[]>(logicalExpression ? logicalExpression?.split(' ') : []);
    const [cursorIndex, setCursorIndex] = useState<number>(tokens ? tokens.length : 0);
    const divRef = useRef<HTMLDivElement>(null);

    const operators = ['(', ')', 'AND', 'OR'];

    const isOperator = (token: string) => operators?.includes(token);

    //   const isConditionEnable = () =>  conditionCount < 26 && ( tokens.length === 0  ||  tokens.at(-1) === '('  || tokens.at(-1) === 'AND' || tokens.at(-1) === 'OR');

    //   const isValidOperator = (operator:string) => {
    //     switch (operator) {
    //         case '(':
    //             return tokens.length === 0  || ( isOperator(tokens.at(-1) as string) && tokens.at(-1) != ')' );
    //         case ')':
    //             return openParenthesisCount > 0 && ( !isOperator(tokens.at(-1) as string) || tokens.at(-1) ===')' );
    //         case 'AND':
    //         case 'OR':
    //             return tokens.length > 0 && (!isOperator(tokens.at(-1) as string) || tokens.at(-1) === ')' );
    //     }
    //   }



    const validateTokens = (tokens: string[]) => {
        let open = 0;
        const visited: Map<string,boolean> = new Map();

        for (let i = 0; i < tokens.length; i++) {
            const prev = tokens[i - 1];
            const curr = tokens[i];

            // Condition
            if (!isOperator(curr)) {
                if (prev && (!isOperator(prev) || prev === ')')) {
                    return { valid: false, error: `Missing operator between conditions. (at position ${i + 1} )` }
                }

                if(visited.get(curr)) {
                    return { valid: false, error: `Conditions can't be repeated. (at position ${i + 1} )` }
                }  
                
                visited.set(curr,true);

                continue;
                
            }

            if (curr === '(') {
                if (prev && (!isOperator(prev) || prev === ')')) {
                    return { valid: false, error: `Invalid "(" placement. (at position ${i + 1})` }
                }
                open++;
            }

            if (curr === ')') {
                if (open === 0) return { valid: false, error: `Unmatched ")". (at position ${i + 1})` }
                if (isOperator(prev!) && prev !== ')') {
                    return { valid: false, error: `Invalid ")" placement. (at position ${i + 1})` }
                }
                open--;
            }

            if (curr === 'AND' || curr === 'OR') {
                if (!prev || (isOperator(prev) && prev !== ')')) {
                    return { valid: false, error: `Invalid logical operator placement. (at position ${i + 1})` }
                }
            }
        }

        if (open !== 0) {
            return { valid: false, error: 'Unclosed "(".' }
        }

        return { valid: true }

    }

    const insertToken = (token: string) => {
        const updated = [
            ...tokens.slice(0, cursorIndex),
            token,
            ...tokens.slice(cursorIndex),
        ];

        setTokens(updated);
        setCursorIndex(cursorIndex + 1);
    }

    const handleBackspace = () => {
        if (cursorIndex === 0) return;

        const updated = [
            ...tokens.slice(0, cursorIndex - 1),
            ...tokens.slice(cursorIndex),
        ];

        setTokens(updated);
        setCursorIndex(cursorIndex - 1);
    }

    // const handleConditionButtonClicked = () => {
    //     if (!nextCondition) return;
    //     insertToken(nextCondition);
    // }

    const handleLogicalExpressionSubmit = () => {
        const {valid,error} = validateTokens(tokens);
        if(!valid) {
            toast.error(error);
            return;
        }
        else {
            handleUpdateLogicalExpression(tokens);
            handleClose();
        }
    }


    const logicalExpressionContainerBtns = [
        {
            buttonId:'advance-rule-engine-logical-expression-submit-btn',
            buttonText: 'Submit',
            btnClassName: 'advance__rule__engine___logical__expression-submit-btn',
            isDisabled: tokens.length == 0,
            buttonVariant: 'filled',
            handleClick: handleLogicalExpressionSubmit
        },
        {
            buttonId:'advance-rule-engine-logical-expression-cancel-btn',
            buttonText: 'Cancel',
            btnClassName: 'advance__rule__engine___logical__expression-cancel-btn',
            isDisabled: false,
            buttonVariant: 'outlined',
            handleClick: handleClose
        }
    ]

    useEffect(() => {
        if (cursorIndex > tokens.length) {
            setCursorIndex(tokens.length);
        }
    }, [tokens.length]);

    const isAlphabetKey = (key: string) => key.length === 1 && (key >= 'a' && key <= 'z' || key >= 'A' && key <= 'Z');

    const operatorKeys = ['(', ')', '&', '|'];
    const isOperatorKey = (key: string) =>  operatorKeys?.includes(key);

    const handleKeyDown = (e) => {
        if (e.key === 'Backspace') {
            e.preventDefault()
            handleBackspace()
        }
        else if (e.key === 'ArrowLeft') {
            if (cursorIndex > 0) setCursorIndex(prev => prev - 1);
        }
        else if (e.key === 'ArrowRight') {
            if (cursorIndex < tokens.length) setCursorIndex(prev => prev + 1);
        }
        else if (isAlphabetKey(e.key)) {
            e.preventDefault()

            const condition = e.key.toUpperCase()

            // Try inserting
            const updated = [
                ...tokens.slice(0, cursorIndex),
                condition,
                ...tokens.slice(cursorIndex),
            ]

            setTokens(updated)
            setCursorIndex(cursorIndex + 1)
        }else if (isOperatorKey(e.key)){
            const idx = operatorKeys?.indexOf(e.key);
            insertToken(operators[idx]);
        }
    }

    useEffectOnce(()=>{
        divRef?.current?.focus()
    })

    return (
        <div className='advance__rule_engine__logical__expression__wrap'
            onClick={(e) => e.stopPropagation()}
            tabIndex={0}
            onKeyDown={handleKeyDown}
            ref={divRef}
        >
            <div className='advance__rule_engine__logical__expression-header'>
                <div className='advance__rule_engine__logical__expression-heading'>Logical Expression</div>
                <div className='advance__rule_engine__logical__expression-description'>Define how condition statements combine using AND, OR, and paranthesis</div>
            </div>
            <div className='advance__rule_engine__logical__expression-container'>
                <div className='advance__rule_engine__logical__expression-label'>Expression</div>
                <div className='advance__rule_engine__logical__expression-value'>
                    {
                        tokens?.length > 0 && tokens.map((token, idx) => {
                            return <React.Fragment key={`token-id-${token}-${idx}`}>
                                {
                                    cursorIndex === idx && <span className="cursor" />
                                }
                                <span className={isOperator(token) ? 'logical__expression__operator' : 'logical__expression__condition-label'}
                                    onClick={() => setCursorIndex(idx + 1)}>{token}</span>
                            </React.Fragment>
                        })
                    }
                    {
                        cursorIndex === tokens.length && <span className="cursor" />
                    }
                </div>
            </div>
            <div className='advance__rule__engine__logical__expression__controls'>
                <div className='advance__rule__engine__logical__expression__input-controls'>
                    {/* {
                        nextCondition && <div className='advance__rule_engine__next-condition-container'>
                            <div className='advance__rule_engine__next-condition-container-label'>Condition</div>
                            <div className='advance__rule_engine__next-condition-container-value'
                                onClick={() => handleConditionButtonClicked()}
                            >{nextCondition}</div>
                        </div>
                    } */}
                    <div className='advance__rule_engine__operator-container'>
                        <div className='advance__rule_engine__operator-container-label'>Operators</div>
                        <div className='advance__rule_engine__operator-container-options'>
                            {
                                operators?.map(operator => {
                                    return <span key={'operator-token-' + operator} className='advance__rule_engine__operator-label'
                                        onClick={() => insertToken(operator)}
                                    >{operator}</span>
                                })
                            }
                        </div>
                    </div>
                </div>
                <div className='advance__rule__engine__logical__expression__btns'>
                    {
                        logicalExpressionContainerBtns?.map(btn=>{
                            return <CustomButton key = {btn.buttonId}
                            buttonText={btn.buttonText} 
                            buttonId={btn.buttonId} 
                            handleClick={btn.handleClick}
                            buttonVariant={btn.buttonVariant}
                            btnClassName={btn.btnClassName} 
                            handleKeyPress={()=>{}}                          
                            />
                        })
                    }
                </div>
            </div>
        </div>
    )
}

export default AddLogicalExpression